/**
 * Root of the navigation tree: owns the state of every navigator, dispatches
 * actions, and keeps the browser URL (react-router history) in sync with it.
 *
 * Ported from @react-navigation/core BaseNavigationContainer and
 * @react-navigation/native NavigationContainer / useLinking (6.x). The URL is
 * derived from state exactly as before, and every state change becomes a
 * history push, a replace, or a `go(-n)` followed by a replace, depending on
 * how the focused navigator's history changed. The E2E journey records assert
 * this step by step.
 */
import * as React from 'react'
import {
  NavigationBuilderContext,
  NavigationContainerRefContext,
  NavigationStateContext,
  ScheduleUpdateContext,
  useChildListeners,
  useEventEmitter,
  useKeyedChildListeners
} from './NavigationBuilder'
import { AppHistory, createMemoryHistory } from './history'
import {
  findFocusedRoute,
  getActionFromState,
  getPathFromState,
  getStateFromPath,
  PathConfig
} from './paths'
import {
  Action,
  CommonActions,
  NavigationState,
  PartialState,
  Route
} from './routers'

type AnyState = NavigationState | PartialState

export interface LinkingOptions {
  enabled?: boolean
  config: PathConfig
}

export interface NavigationContainerRef {
  dispatch: (action: Action) => void
  resetRoot: (state?: AnyState) => void
  getRootState: () => NavigationState | undefined
  getCurrentRoute: () => Route | undefined
  addListener: (type: string, callback: (e: any) => void) => () => void
  [helper: string]: any
}

const NOT_INITIALIZED_ERROR =
  "The 'navigation' object hasn't been initialized yet. This might happen if you don't have a navigator mounted, or if the navigator hasn't finished mounting."

const getPartialState = (
  state: AnyState | undefined
): PartialState | undefined => {
  if (state === undefined) {
    return undefined
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { key, routeNames, ...partialState } = state as NavigationState

  return {
    ...(partialState as any),
    stale: true,
    routes: state.routes.map(route => {
      if (route.state === undefined) {
        return route
      }

      return { ...route, state: getPartialState(route.state) }
    })
  }
}

const UNINITIALIZED_STATE = {}

/** A state container whose value is readable synchronously (see useNavigationBuilder's useScheduleUpdate). */
function useSyncState<T>(initialState: () => T) {
  const stateRef = React.useRef<T>(UNINITIALIZED_STATE as any)
  const isSchedulingRef = React.useRef(false)
  const isMountedRef = React.useRef(true)

  React.useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  if (stateRef.current === UNINITIALIZED_STATE) {
    stateRef.current = initialState()
  }

  const [trackingState, setTrackingState] = React.useState(stateRef.current)

  const getState = React.useCallback(() => stateRef.current, [])

  const setState = React.useCallback((state: T) => {
    if (state === stateRef.current || !isMountedRef.current) {
      return
    }

    stateRef.current = state

    if (!isSchedulingRef.current) {
      setTrackingState(state)
    }
  }, [])

  const scheduleUpdate = React.useCallback((callback: () => void) => {
    isSchedulingRef.current = true

    try {
      callback()
    } finally {
      isSchedulingRef.current = false
    }
  }, [])

  const flushUpdates = React.useCallback(() => {
    if (!isMountedRef.current) {
      return
    }

    // Make sure that the tracking state is up-to-date.
    // We call it unconditionally, but React should skip the update if state is unchanged.
    setTrackingState(stateRef.current)
  }, [])

  // If we're rendering and the tracking state is out of date, update it immediately
  // This will make sure that our updates are applied as early as possible.
  if (trackingState !== stateRef.current) {
    setTrackingState(stateRef.current)
  }

  const state = stateRef.current

  return [state, getState, setState, scheduleUpdate, flushUpdates] as const
}

// ---------------------------------------------------------------------------
// BaseNavigationContainer
// ---------------------------------------------------------------------------

interface BaseContainerProps {
  initialState?: AnyState
  onStateChange?: (state: NavigationState | undefined) => void
  children: React.ReactNode
}

const BaseNavigationContainer = React.forwardRef<
  NavigationContainerRef,
  BaseContainerProps
>(function BaseNavigationContainer(
  { initialState, onStateChange, children },
  ref
) {
  const [
    state,
    getState,
    setState,
    scheduleUpdate,
    flushUpdates
  ] = useSyncState<AnyState | undefined>(() =>
    getPartialState(initialState == null ? undefined : initialState)
  )

  const navigatorKeyRef = React.useRef<string | undefined>()

  const getKey = React.useCallback(() => navigatorKeyRef.current, [])

  const setKey = React.useCallback((key: string) => {
    navigatorKeyRef.current = key
  }, [])

  const { listeners, addListener } = useChildListeners()

  const { keyedListeners, addKeyedListener } = useKeyedChildListeners()

  const dispatch = React.useCallback(
    (action: Action) => {
      if (listeners.focus[0] == null) {
        console.error(NOT_INITIALIZED_ERROR)
      } else {
        listeners.focus[0](navigation => navigation.dispatch(action))
      }
    },
    [listeners.focus]
  )

  const canGoBack = React.useCallback(() => {
    if (listeners.focus[0] == null) {
      return false
    }

    const { result, handled } = listeners.focus[0](navigation =>
      navigation.canGoBack()
    )

    return handled ? result : false
  }, [listeners.focus])

  const resetRoot = React.useCallback(
    (state?: AnyState) => {
      const target = state?.key ?? keyedListeners.getState.root?.()?.key

      if (target == null) {
        console.error(NOT_INITIALIZED_ERROR)
      } else {
        listeners.focus[0](navigation =>
          navigation.dispatch({
            ...CommonActions.reset(state),
            target
          })
        )
      }
    },
    [keyedListeners.getState, listeners.focus]
  )

  const getRootState = React.useCallback(() => {
    return keyedListeners.getState.root?.() as NavigationState | undefined
  }, [keyedListeners.getState])

  const getCurrentRoute = React.useCallback(() => {
    const state = getRootState()

    if (state == null) {
      return undefined
    }

    return findFocusedRoute(state) as Route | undefined
  }, [getRootState])

  const emitter = useEventEmitter()

  const navigation: NavigationContainerRef = React.useMemo(
    () => ({
      ...Object.keys(CommonActions).reduce<any>((acc, name) => {
        acc[name] = (...args: any[]) =>
          dispatch((CommonActions as any)[name](...args))
        return acc
      }, {}),
      ...emitter.create('root'),
      dispatch,
      resetRoot,
      isFocused: () => true,
      canGoBack,
      getParent: () => undefined,
      getState: () => getState(),
      getRootState,
      getCurrentRoute,
      isReady: () => listeners.focus[0] != null
    }),
    [
      canGoBack,
      dispatch,
      emitter,
      getCurrentRoute,
      getRootState,
      getState,
      listeners.focus,
      resetRoot
    ]
  )

  React.useImperativeHandle(ref, () => navigation, [navigation])

  const onUnhandledAction = React.useCallback((action: Action) => {
    if (process.env.NODE_ENV === 'production') {
      return
    }

    const payload: Record<string, any> | undefined = action.payload

    let message = `The action '${action.type}'${
      payload ? ` with payload ${JSON.stringify(action.payload)}` : ''
    } was not handled by any navigator.`

    switch (action.type) {
      case 'NAVIGATE':
      case 'PUSH':
      case 'REPLACE':
      case 'JUMP_TO':
        if (payload?.name) {
          message += `\n\nDo you have a screen named '${payload.name}'?`
        } else {
          message += `\n\nYou need to pass the name of the screen to navigate to.`
        }
        break
      case 'GO_BACK':
      case 'POP':
      case 'POP_TO_TOP':
        message += `\n\nIs there any screen to go back to?`
        break
      case 'OPEN_DRAWER':
      case 'CLOSE_DRAWER':
      case 'TOGGLE_DRAWER':
        message += `\n\nIs your screen inside a Drawer navigator?`
        break
    }

    message += `\n\nThis is a development-only warning and won't be shown in production.`

    console.error(message)
  }, [])

  const builderContext = React.useMemo(
    () => ({ addListener, addKeyedListener, onUnhandledAction }),
    [addListener, addKeyedListener, onUnhandledAction]
  )

  const scheduleContext = React.useMemo(
    () => ({ scheduleUpdate, flushUpdates }),
    [scheduleUpdate, flushUpdates]
  )

  const isInitialRef = React.useRef(true)

  const getIsInitial = React.useCallback(() => isInitialRef.current, [])

  const context = React.useMemo(
    () => ({ state, getState, setState, getKey, setKey, getIsInitial }),
    [state, getState, setState, getKey, setKey, getIsInitial]
  )

  const isFirstMountRef = React.useRef(true)
  const onStateChangeRef = React.useRef(onStateChange)

  React.useEffect(() => {
    isInitialRef.current = false
    onStateChangeRef.current = onStateChange
  })

  React.useEffect(() => {
    const hydratedState = getRootState()

    emitter.emit({ type: 'state', data: { state } })

    if (!isFirstMountRef.current && onStateChangeRef.current) {
      onStateChangeRef.current(hydratedState)
    }

    isFirstMountRef.current = false
  }, [getRootState, emitter, state])

  return (
    <NavigationContainerRefContext.Provider value={navigation}>
      <ScheduleUpdateContext.Provider value={scheduleContext}>
        <NavigationBuilderContext.Provider value={builderContext}>
          <NavigationStateContext.Provider value={context}>
            {children}
          </NavigationStateContext.Provider>
        </NavigationBuilderContext.Provider>
      </ScheduleUpdateContext.Provider>
    </NavigationContainerRefContext.Provider>
  )
})

// ---------------------------------------------------------------------------
// Linking
// ---------------------------------------------------------------------------

/**
 * Find the matching navigation state that changed between 2 navigation states
 * e.g.: a -> b -> c -> d and a -> b -> c -> e -> f, if history in b changed, b is the matching state
 */
const findMatchingState = (
  a: NavigationState | undefined,
  b: NavigationState | undefined
): [NavigationState | undefined, NavigationState | undefined] => {
  if (a === undefined || b === undefined || a.key !== b.key) {
    return [undefined, undefined]
  }

  // Tab and drawer will have `history` property, but stack will have history in `routes`
  const aHistoryLength = a.history ? a.history.length : a.routes.length
  const bHistoryLength = b.history ? b.history.length : b.routes.length

  const aRoute = a.routes[a.index]
  const bRoute = b.routes[b.index]

  const aChildState = aRoute.state as NavigationState | undefined
  const bChildState = bRoute.state as NavigationState | undefined

  // Stop here if this is the state object that changed:
  // - history length is different
  // - focused routes are different
  // - one of them doesn't have child state
  // - child state keys are different
  if (
    aHistoryLength !== bHistoryLength ||
    aRoute.key !== bRoute.key ||
    aChildState === undefined ||
    bChildState === undefined ||
    aChildState.key !== bChildState.key
  ) {
    return [a, b]
  }

  return findMatchingState(aChildState, bChildState)
}

/**
 * Run async function in series as it's called.
 */
const series = (cb: () => Promise<void>) => {
  // Whether we're currently handling a callback
  let handling = false
  let queue: (() => Promise<void>)[] = []

  const callback = async () => {
    try {
      if (handling) {
        // If we're currently handling a previous event, wait before handling this one
        // Add the callback to the beginning of the queue
        queue.unshift(callback)
        return
      }

      handling = true

      await cb()
    } finally {
      handling = false

      if (queue.length) {
        // If we have queued items, handle the last one
        const last = queue.pop()

        last?.()
      }
    }
  }

  return callback
}

const isDeepEqual = (a: any, b: any): boolean => {
  if (a === b) return true
  if (a == null || b == null || typeof a !== 'object' || typeof b !== 'object')
    return false
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  return (
    aKeys.length === bKeys.length &&
    aKeys.every(key => isDeepEqual(a[key], b[key]))
  )
}

function useLinking(
  ref: React.RefObject<NavigationContainerRef>,
  browserHistory: AppHistory | undefined,
  { enabled = true, config }: LinkingOptions
) {
  const [history] = React.useState(() =>
    browserHistory ? createMemoryHistory(browserHistory) : undefined
  )

  const enabledRef = React.useRef(enabled)
  const configRef = React.useRef(config)

  React.useEffect(() => {
    enabledRef.current = enabled
    configRef.current = config
  })

  const getInitialState = React.useCallback(() => {
    if (!enabledRef.current || !history) {
      return undefined
    }
    const path = window.location.pathname + window.location.search
    return path ? getStateFromPath(path, configRef.current) : undefined
  }, [history])

  const previousStateRef = React.useRef<NavigationState | undefined>(undefined)
  const pendingPopStatePathRef = React.useRef<string | undefined>(undefined)
  const previousIndexRef = React.useRef<number | undefined>(undefined)

  React.useEffect(() => {
    if (!history) {
      return undefined
    }

    previousIndexRef.current = history.index

    return history.listen(() => {
      const navigation = ref.current

      if (!navigation || !enabled) {
        return
      }

      const path = window.location.pathname + window.location.search
      const index = history.index

      const previousIndex = previousIndexRef.current ?? 0

      previousIndexRef.current = index
      pendingPopStatePathRef.current = path

      // When browser back/forward is clicked, we first need to check if state object for this index exists
      // If it does we'll reset to that state object
      // Otherwise, we'll handle it like a regular deep link
      const record = history.get(index)

      if (record?.path === path && record?.state) {
        navigation.resetRoot(record.state)
        return
      }

      const state = getStateFromPath(path, configRef.current)

      // We should only dispatch an action when going forward
      // Otherwise the action will likely add items to history, which would mess things up
      if (state) {
        // Make sure that the routes in the state exist in the root navigator
        // Otherwise there's an error in the linking configuration
        const rootState = navigation.getRootState()

        if (state.routes.some(r => !rootState?.routeNames.includes(r.name))) {
          console.warn(
            "The navigation state parsed from the URL contains routes not present in the root navigator. This usually means that the linking configuration doesn't match the navigation structure."
          )
          return
        }

        if (index > previousIndex) {
          const action = getActionFromState(state)

          if (action !== undefined) {
            try {
              navigation.dispatch(action)
            } catch (e) {
              // Ignore any errors from deep linking.
              // This could happen in case of malformed links, navigation object not being initialized etc.
              console.warn(
                `An error occurred when trying to handle the link '${path}': ${
                  (e as any)?.message ?? e
                }`
              )
            }
          } else {
            navigation.resetRoot(state)
          }
        } else {
          navigation.resetRoot(state)
        }
      } else {
        // if current path didn't return any state, we should revert to initial state
        navigation.resetRoot(state)
      }
    })
  }, [enabled, history, ref])

  React.useEffect(() => {
    if (!enabled || !history) {
      return undefined
    }

    const getPathForRoute = (
      route: ReturnType<typeof findFocusedRoute>,
      state: NavigationState
    ): string => {
      // If the `route` object contains a `path`, use that path as long as `route.name` and `params` still match
      // This makes sure that we preserve the original URL for wildcard routes
      if (route?.path) {
        const stateForPath = getStateFromPath(route.path, configRef.current)

        if (stateForPath) {
          const focusedRoute = findFocusedRoute(stateForPath)

          if (
            focusedRoute &&
            focusedRoute.name === route.name &&
            isDeepEqual(focusedRoute.params, route.params)
          ) {
            return route.path
          }
        }
      }

      return getPathFromState(state, configRef.current)
    }

    if (ref.current) {
      // We need to record the current metadata on the first render if they aren't set
      // This will allow the initial state to be in the history entry
      const state = ref.current.getRootState()

      if (state) {
        const route = findFocusedRoute(state)
        const path = getPathForRoute(route, state)

        if (previousStateRef.current === undefined) {
          previousStateRef.current = state
        }

        history.replace({ path, state })
      }
    }

    const onStateChange = async () => {
      const navigation = ref.current

      if (!navigation || !enabled) {
        return
      }

      const previousState = previousStateRef.current
      const state = navigation.getRootState()

      // root state may not available, for example when root navigators switch inside the container
      if (!state) {
        return
      }

      const pendingPath = pendingPopStatePathRef.current
      const route = findFocusedRoute(state)
      const path = getPathForRoute(route, state)

      previousStateRef.current = state
      pendingPopStatePathRef.current = undefined

      // To detect the kind of state change, we need to:
      // - Find the common focused navigation state in previous and current state
      // - If only the route keys changed, compare history/routes.length to check if we go back/forward/replace
      // - If no common focused navigation state found, it's a replace
      const [previousFocusedState, focusedState] = findMatchingState(
        previousState,
        state
      )

      if (
        previousFocusedState &&
        focusedState &&
        // We should only handle push/pop if path changed from what was in last `popstate`
        // Otherwise it's likely a change triggered by `popstate`
        path !== pendingPath
      ) {
        const historyDelta =
          (focusedState.history
            ? focusedState.history.length
            : focusedState.routes.length) -
          (previousFocusedState.history
            ? previousFocusedState.history.length
            : previousFocusedState.routes.length)

        if (historyDelta > 0) {
          // If history length is increased, we should pushState
          // Note that path might not actually change here, for example, drawer open should pushState
          history.push({ path, state })
        } else if (historyDelta < 0) {
          // If history length is decreased, i.e. entries were removed, we want to go back

          const nextIndex = history.backIndex({ path })
          const currentIndex = history.index

          try {
            if (nextIndex !== -1 && nextIndex < currentIndex) {
              // An existing entry for this path exists and it's less than current index, go back to that
              await history.go(nextIndex - currentIndex)
            } else {
              // We couldn't find an existing entry to go back to, so we'll go back by the delta
              // This won't be correct if multiple routes were pushed in one go before
              // Usually this shouldn't happen and this is a fallback for that
              await history.go(historyDelta)
            }

            // Store the updated state as well as fix the path if incorrect
            history.replace({ path, state })
          } catch (e) {
            // The navigation was interrupted
          }
        } else {
          // If history length is unchanged, we want to replaceState
          history.replace({ path, state })
        }
      } else {
        // If no common navigation state was found, assume it's a replace
        // This would happen if the user did a reset/conditionally changed navigators
        history.replace({ path, state })
      }
    }

    // We debounce onStateChange coz we don't want multiple state changes to be handled at one time
    // This could happen since `history.go(n)` is asynchronous
    // If `pushState` or `replaceState` were called before `history.go(n)` completes, it'll mess stuff up
    return ref.current?.addListener('state', series(onStateChange))
  }, [enabled, history, ref])

  return { getInitialState }
}

// ---------------------------------------------------------------------------
// NavigationContainer
// ---------------------------------------------------------------------------

export interface NavigationContainerProps {
  /** Browser history shared with react-router; required for linking. */
  history?: AppHistory
  linking?: LinkingOptions
  initialState?: AnyState
  /** Set document.title to the focused screen name (the previous behaviour). */
  documentTitle?: { enabled?: boolean }
  onStateChange?: (state: NavigationState | undefined) => void
  children: React.ReactNode
}

export const NavigationContainer = React.forwardRef<
  NavigationContainerRef,
  NavigationContainerProps
>(function NavigationContainer(
  { history, linking, initialState, documentTitle, onStateChange, children },
  ref
) {
  const isLinkingEnabled = linking ? linking.enabled !== false : false

  const refContainer = React.useRef<NavigationContainerRef>(null)

  const { getInitialState } = useLinking(refContainer, history, {
    enabled: isLinkingEnabled && !!history,
    config: linking?.config ?? { screens: {} }
  })

  const [linkingInitialState] = React.useState(getInitialState)

  React.useImperativeHandle(
    ref,
    () => refContainer.current as NavigationContainerRef
  )

  // Title: the focused route's name, updated whenever the navigation state changes.
  const titleEnabled = documentTitle?.enabled !== false
  React.useEffect(() => {
    if (!titleEnabled) {
      return undefined
    }
    const navigation = refContainer.current
    const update = () => {
      const route = navigation?.getCurrentRoute()
      if (route?.name) {
        document.title = route.name
      }
    }
    update()
    return navigation?.addListener('state', update)
  }, [titleEnabled])

  return (
    <BaseNavigationContainer
      initialState={initialState == null ? linkingInitialState : initialState}
      onStateChange={onStateChange}
      ref={refContainer}
    >
      {children}
    </BaseNavigationContainer>
  )
})
